import {
  Component,
  EventEmitter,
  HostListener,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { combineLatest, Subscription } from 'rxjs';
import {
  ICasePayment,
  IServiceInformationItem,
  INewCasePayment,
} from '../../interfaces/case-card-interfaces';
import { AppMessageService } from '../../../../../../shared/services/app-message/app-message.service';
import { Store } from '@ngrx/store';
import { AppAlertService } from '../../../../../../shared/services/app-alert/app-alert.service';
import { TranslateService } from '@ngx-translate/core';
import { Option } from '../../../../../../ui-components/core-dropdown/core-dropdown.component';
import {
  selectCaseApproved,
  selectCaseDate,
  selectCaseId,
  selectCaseType,
  selectReadonly,
} from '../../../../store/selectors/common-case.selector';

import Decimal from 'decimal.js';
import { CasePaymentFormComponent } from './components/case-payment-form/case-payment-form.component';
import { selectPaymentInformations } from '../../../../store/selectors/payment-informations.selector';
import { selectServiceInformations } from '../../../../store/selectors/service-informations.selector';
import { CaseService } from '../../../../services/case.services';
import { deletePaymentInformation } from '../../../../store/actions/service-payment.actions';
import { CreatePaymentReceiverPopupComponent } from './components/create-payment-receiver-popup/create-payment-receiver-popup.component';

@Component({
  selector: 'app-payment-informations',

  templateUrl: './payment-informations.component.html',
  styleUrl: './payment-informations.component.scss',
})
export class PaymentInformationsComponent implements OnInit, OnDestroy{
  @Output() formChanged = new EventEmitter();
  @ViewChild('paymentForm') paymentForm: CasePaymentFormComponent;
  @ViewChild('paymentReceiver') paymentReceiver: CreatePaymentReceiverPopupComponent;
  private subscription!: Subscription;
  payments: ICasePayment[] = [];
  isReadOnly: boolean = false;
  isFooterExpanded: boolean = false;
  lastScrollTop = 0;
  institutions: Option[];
  filteredServices: IServiceInformationItem[] = [];
  isCaseApproved: boolean | null;
  caseId: number | null;
  caseDate: Date | null;
  caseType: any;
  detailInfo: {
    totalMustPaid: number | null;
    paid: number | null;
    mustPaid: number | null;
    totalInvoicedAmount: number | null;
    invoicedAmount: number | null;
    mustInvoicedAmount: number | null;
  } = {
    totalMustPaid: null,
    paid: null,
    mustPaid: null,
    totalInvoicedAmount: null,
    invoicedAmount: null,
    mustInvoicedAmount: null,
  };

  constructor(
    private readonly store: Store,
    private readonly messageService: AppMessageService,
    private readonly appAlertService: AppAlertService,
    private readonly translate: TranslateService,
    private readonly caseService: CaseService
  ) {}

  ngOnInit(): void {
    this.subscription = combineLatest([
      this.store.select(selectPaymentInformations),
      this.store.select(selectReadonly),
      this.store.select(selectCaseApproved),
      this.store.select(selectCaseId),
      this.store.select(selectCaseType),
      this.store.select(selectServiceInformations),
      this.store.select(selectCaseDate),
    ]).subscribe({
      next: ([
        selectPaymentInformations,
        isReadOnly,
        isApproved,
        caseId,
        caseType,
        serviceInformations,
        caseDate,
      ]) => {
        this.isReadOnly = isReadOnly;
        this.isCaseApproved = isApproved ?? false;
        this.caseId = caseId ?? null;
        this.caseType = caseType ?? null;
        this.payments = selectPaymentInformations ?? [];
        this.caseDate = caseDate;
        this.setSummary(serviceInformations);
      },
    });
  }

  setSummary(serviceInformations: any) {
    this.detailInfo.totalMustPaid = serviceInformations?.lastPrice ?? null;
    this.detailInfo.totalInvoicedAmount = serviceInformations?.lastPrice;

    this.detailInfo.paid =
      this.payments
        .reduce(
          (sum, item) => this.toDecimalSafe(item?.totalTL).plus(sum),
          new Decimal(0)
        )
        .toNumber() || null;

    this.detailInfo.mustPaid =
      this.toDecimalSafe(this.detailInfo?.totalMustPaid)
        .minus(this.toDecimalSafe(this.detailInfo?.paid))
        .toNumber() ?? null;
  }
  trackByFn(index: number, item: ICasePayment): number {
    return item.id;
  }

  deletePayment(payment: ICasePayment): void {
    this.appAlertService.sweetConfirm({
      header: this.translate.instant('case.payment-informations.sure-header'),
      message: this.translate.instant('case.payment-informations.sure-text'),
      showCloseButton: true, 
      accept: () => {
        this.caseService.deleteCasePayment(this.caseId!, payment.id).subscribe({
          next: (res) => {
            if (res.success) {
              this.store.dispatch(
                deletePaymentInformation({
                  paymentId: payment.id,
                })
              );
              this.messageService.successToast(
                this.translate.instant(
                  'case.payment-informations.delete-success-header'
                ),
                this.translate.instant(
                  'case.payment-informations.delete-success-text'
                )
              );
            }
          },
        });
      },
    });
  }

  openFormPopup(payment?: ICasePayment) {
    const today = new Date();
    const caseDateObj = this.caseDate ? new Date(this.caseDate) : null;

    const isOlderThan7Days =
      caseDateObj &&
      (today.getTime() - caseDateObj.getTime()) / (1000 * 60 * 60 * 24) > 7;

    this.paymentForm.openFormPopup(
      this.caseId,
      this.caseType?.valueObject?.id?.value,
      isOlderThan7Days ? null:this.caseDate,
      payment
    );
  }

  toDecimalSafe(value: number | null): Decimal {
    return new Decimal(value ?? 0); // null ise 0 olarak kabul et
  }

  @HostListener('window:scroll', ['$event'])
  onScroll() {
    const windowHeight = window.innerHeight;
    const documentHeight = document.documentElement.scrollHeight;
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

    if (documentHeight - (scrollTop + windowHeight) < 30) {
      this.isFooterExpanded = true;
    } else if (scrollTop < this.lastScrollTop) {
      // Yukarı kaydırılıyorsa footer'ı küçült
      this.isFooterExpanded = false;
    }

    this.lastScrollTop = Math.max(0, scrollTop);
  }

  toggleFooter() {
    this.isFooterExpanded = !this.isFooterExpanded;
  }

  getFormValues() {
    return null;
  }

  servicesChanged() {
    this.formChanged.emit();
  }

  onSave(payment: INewCasePayment) {}

  openPaymentReceiverPopup(){
    this.paymentReceiver.initilize(this.caseId!)
  }
  @HostListener('window:beforeunload', ['$event'])
  onBeforeUnload(event: Event): void {
    this.unSub();
  }

  unSub() {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }
  ngOnDestroy(): void {
    this.unSub();
  }
}
